import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../api/index";
import {
  fetchOnboardingState,
  isOnboardingActive,
} from "../api/onboarding";
import {
  getCurrentUser,
  getCurrentUserId,
  isAuthenticated,
  isEmailNotVerifiedError,
  isEmailVerified,
  logout,
  PENDING_VERIFICATION_EMAIL_KEY,
  updateUser,
} from "../utils/auth";

export const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isResending, setIsResending] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState<string | null>(
    searchParams.get("error"),
  );

  const email =
    getCurrentUser()?.email ||
    sessionStorage.getItem(PENDING_VERIFICATION_EMAIL_KEY) ||
    "";

  useEffect(() => {
    if (isAuthenticated() && isEmailVerified()) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleResend = async () => {
    if (!email) {
      setError("No email address found. Please sign in again.");
      return;
    }

    setIsResending(true);
    setError(null);
    setStatus("");

    try {
      const response = await fetch(`${API_BASE_URL}/user/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (data.success) {
        setStatus("Verification email sent. Check your inbox.");
      } else {
        setError(
          data.error?.message ||
            data.message ||
            "Failed to send verification email",
        );
      }
    } catch (err) {
      setError("Network error: " + (err as Error).message);
    } finally {
      setIsResending(false);
    }
  };

  const handleSwitchAccount = () => {
    // Clear session so LoginPage does not bounce authenticated users back
    // to /dashboard (and then back here via ProtectedRoute).
    logout();
    navigate("/login", { replace: true });
  };

  const handleDeleteAccount = async () => {
    const userId = getCurrentUserId();
    if (!userId) {
      setError("Please sign in again to delete your account.");
      return;
    }

    const confirmText = "DELETE";
    const userInput = window.prompt(
      `This will permanently delete your account and free this email address.\n\nType "${confirmText}" to confirm:`,
    );
    if (userInput !== confirmText) {
      return;
    }

    setIsDeleting(true);
    setError(null);
    setStatus("");

    try {
      const response = await fetch(`${API_BASE_URL}/user/${userId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmDeletion: true }),
      });
      const data = await response.json();

      if (data.success) {
        logout();
        navigate("/", { replace: true });
        return;
      }

      setError(
        data.error?.message || data.message || "Failed to delete account",
      );
    } catch (err) {
      setError("Network error: " + (err as Error).message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleContinue = async () => {
    if (!isAuthenticated()) {
      navigate("/login");
      return;
    }

    const userId = getCurrentUserId();
    if (!userId) {
      navigate("/login");
      return;
    }

    setIsChecking(true);
    setError(null);
    setStatus("");

    const result = await fetchOnboardingState(userId);
    setIsChecking(false);

    if (!result.ok) {
      if (isEmailNotVerifiedError(result.error)) {
        setError("Your email is not verified yet. Check your inbox and try again.");
        return;
      }
      setError(result.error.message);
      return;
    }

    updateUser({ verified: true });
    sessionStorage.removeItem(PENDING_VERIFICATION_EMAIL_KEY);
    navigate(isOnboardingActive(result.value) ? "/setup" : "/dashboard", {
      replace: true,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Verify your email
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          We sent a verification link
          {email ? (
            <>
              {" "}
              to <span className="font-medium text-gray-900">{email}</span>
            </>
          ) : (
            " to your inbox"
          )}
          . You need to verify before you can use SyncCal.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          )}
          {status && (
            <div className="rounded-md bg-green-50 p-4">
              <p className="text-sm font-medium text-green-800">{status}</p>
            </div>
          )}

          <button
            type="button"
            onClick={handleResend}
            disabled={isResending || !email}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isResending ? "Sending..." : "Resend verification email"}
          </button>

          <button
            type="button"
            onClick={handleContinue}
            disabled={isChecking}
            className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isChecking ? "Checking..." : "I've verified my email"}
          </button>

          <p className="text-center text-sm text-gray-600">
            Wrong account?{" "}
            <button
              type="button"
              onClick={handleSwitchAccount}
              className="font-medium text-indigo-600 hover:text-indigo-500 underline underline-offset-2"
            >
              Sign in
            </button>
            {isAuthenticated() && (
              <>
                {" "}
                or{" "}
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="font-medium text-red-600 hover:text-red-500 underline underline-offset-2 disabled:opacity-50"
                >
                  {isDeleting ? "Deleting..." : "delete this account"}
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};
